import React, { useState, useEffect, useRef } from "react";
import { useUser } from "../useUser";
import AdminLineItem from "@/components/AdminLineItem";
import ConfirmModal from "@/components/ConfirmModal";
import { ProgressBar, Table, Badge, Form, Spinner } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import { useAuthenticatedApi } from "@/utils/callApi";
import { useMsal } from "@azure/msal-react";
import styles from "@/styles/Gradients.module.css";
import useProgressLabel from "../useProgressLabel";
import SearchableSelect from "@/components/SearchableSelect";
import { BrandButton as Button } from "@/components";
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';

// Brand color constants for consistency
const BRAND = {
  PRIMARY: "#F16D09",    // Primary buttons, accents
  ALERT: "#DC3D17",      // Alerts, emphasis
  BACKGROUND: "#FCED21", // Backgrounds, highlights
  CARD: "#FFC60B",       // Cards, accents
  DARK: "#2E1719",       // Headings, dark cards
  BODY: "#492C28",       // Body, subcards
};

export default function SurveyMonitor() {
  const user = useUser();
  const { secureHelpers } = useAuthenticatedApi();
  const { inProgress } = useMsal();
  
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyGroups, setSurveyGroups] = useState([]);
  const [hospiceInstances, setHospiceInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const instanceDetailsRef = useRef(null);
  const [displayLimit, setDisplayLimit] = useState(12);
  const getProgressLabel = useProgressLabel();

  // Search / filter state
  const [titleSearch, setTitleSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsFilter, setTagsFilter] = useState([]); // changing this triggers a server re-fetch
  const [includeClosed, setIncludeClosed] = useState(false); // changing this triggers a server re-fetch
 
  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null
  });
  
  // Filter by title (client-side), paginated by displayLimit
  // Tag filtering is server-side (tagsFilter triggers re-fetch via useEffect)
  const titleFilteredSurveys = surveyGroups.filter(g =>
    !titleSearch || g.surveyTitle?.toLowerCase().includes(titleSearch.toLowerCase())
  );
  const displayedSurveys = titleFilteredSurveys.slice(0, displayLimit);
    
  // Load roles from API
  useEffect(() => {
    const loadRoles = async () => {
      if (!secureHelpers?.get || permissions.length > 0) {
        return;
      }
      
      try {
        const data = await secureHelpers.get('/api/roles');
        const transformedPermissions = data.map(role => ({
          id: role.id, // String identifier like 'patient_surveys'
          label: role.name,
          description: role.description || ''
        }));
        setPermissions(transformedPermissions);
      } catch (err) {
        console.error('Failed to load roles:', err);
      }
    };
    
    loadRoles();
  }, [permissions.length]);

  // Load all available tags once for the tag filter dropdown
  useEffect(() => {
    if (!secureHelpers?.get || availableTags.length > 0) return;
    secureHelpers.get('/api/Surveys/tags')
      .then(tags => setAvailableTags(Array.isArray(tags) ? tags : []))
      .catch(err => console.error('Failed to load available tags:', err));
  }, [availableTags.length]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Load survey groups from API on mount
  useEffect(() => {
    const getAllSurveyGroups = async () => {
      // Wait for MSAL to finish any pending operations
      if (inProgress !== "none") {
        console.log('MSAL operation in progress, waiting...');
        return;
      }
      
      if (!secureHelpers?.admin?.getSurveyInstanceGroups) {
        console.log('secureHelpers not ready yet');
        return;
      }
      
      try {
        setLoading(true);
        // Get survey groups filtered by selected tags (server-side AND filter).
        // With no tags: backend returns only active/open surveys (default behaviour).
        // With tags: backend returns ALL surveys matching those tags (bypasses AnyOpen filter).
        const groupData = await secureHelpers.admin.getSurveyInstanceGroups(tagsFilter, includeClosed);
        // Handle C# $values serialization if present
        const groups = Array.isArray(groupData) ? groupData : (groupData?.$values || []);
        
        // LOG RAW BACKEND DATA FOR DEBUGGING PROGRESS MISMATCH
        console.log('=== RAW BACKEND DATA FOR ALL SURVEYS ===');
        groups.forEach(survey => {
          const notStartedItem = survey.progress.find(p => p.key === 0 || p.key === "NotStarted");
          const inProgressItem = survey.progress.find(p => p.key === 1 || p.key === "InProgress");
          // Backend inconsistently sends "Complete" OR "Completed" - handle both!
          const completedItem = survey.progress.find(p => p.key === 2 || p.key === "Completed" || p.key === "Complete");
          
          const notStarted = notStartedItem?.value || 0;
          const inProgress = inProgressItem?.value || 0;
          const completed = completedItem?.value || 0;
          const progressTotal = notStarted + inProgress + completed;
          
          console.log(`\n ${survey.surveyTitle}`);
          console.log(`   Total Instances (from backend): ${survey.instanceCount}`);
          console.log(`   Raw Progress Array:`, survey.progress);
          console.log(`   NotStarted: ${notStarted}`);
          console.log(`   InProgress: ${inProgress}`);
          console.log(`   Completed: ${completed}`);
          console.log(`   Progress Total: ${progressTotal}`);
          if (progressTotal !== survey.instanceCount) {
            console.log(`   ⚠️ MISMATCH: Progress adds to ${progressTotal} but instanceCount is ${survey.instanceCount}`);
          }
        });
        console.log('=== END RAW BACKEND DATA ===\n');
        
        // Don't fetch roles for all surveys upfront — lazy-load when a survey is selected
        setSurveyGroups(groups);
        if (groups && groups.length > 0) {
          setSelectedSurvey(groups[0]);
        }
      } catch (err) {
        console.error('Failed to load survey group data:', err);
        // Keep empty array on error
      } finally {
        setLoading(false);
      }
    };
  
    getAllSurveyGroups();
  }, [inProgress, tagsFilter, includeClosed]); // tagsFilter / includeClosed changes trigger server re-fetch; secureHelpers omitted to prevent loop

  // Load hospice instances when a survey group is selected (also lazy-loads roles for the selected survey)
  useEffect(() => {
    const loadHospiceInstances = async () => {
      // Wait for MSAL to finish any pending operations
      if (inProgress !== "none") {
        return;
      }
      
      if (!secureHelpers?.admin?.getSurveyInstancesByGroup || !selectedSurvey) {
        return;
      }

      // Lazy-load roles for this survey if not already loaded
      if (!selectedSurvey.roles) {
        try {
          const decodedKey = JSON.parse(atob(selectedSurvey.key));
          const surveyId = decodedKey.SurveyId;
          const fullSurvey = await secureHelpers.get(`/api/Surveys/${surveyId}`);
          const roles = fullSurvey.roles || fullSurvey.Roles || [];
          // Update both the surveyGroups list and the selectedSurvey with roles
          setSurveyGroups(prev => prev.map(g => g.key === selectedSurvey.key ? { ...g, roles } : g));
          setSelectedSurvey(prev => ({ ...prev, roles }));
        } catch (err) {
          console.error('Failed to load roles for selected survey:', err);
        }
      }
      
      try {
        // Get instances (hospice assignments) for the selected survey group
        const instances = await secureHelpers.admin.getSurveyInstancesByGroup(selectedSurvey.key);
        
        // Map instances to the format expected by the UI
        const mappedResponses = instances.map(instance => ({
          instanceId: instance.id, // Actual instance ID for API calls
          userId: instance.id, // Keep for backwards compatibility
          name: instance.hospiceName || "Unknown Hospice",
          email: "N/A", // API doesn't include email
          hospice: instance.hospiceName || "Unknown",
          // Store raw progress status from backend (enum: 0=NotStarted, 1=InProgress, 2=Complete)
          progressStatus: instance.progress,
          lastActive: instance.lastModified || null,
          demographic: "N/A", // API doesn't include demographic
          isOpen: instance.isOpen // Open/closed state
        }));
        
        setHospiceInstances(mappedResponses);
        console.log('Loaded hospice instances:', mappedResponses);
      } catch (err) {
        console.error('Failed to load hospice instances:', err);
        // Clear instances on error
        setHospiceInstances([]);
      }
    };
    
    loadHospiceInstances();
  }, [selectedSurvey, inProgress]); // Load when survey changes
  
  // Calculate survey metrics
  // Helper to format dates nicely
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  // Handle survey selection
  const handleSurveySelect = (surveyKey) => {
    const selected = surveyGroups.find(s => s.key === surveyKey);
    setSelectedSurvey(selected || null);
    
    // Smooth scroll to instance details section
    setTimeout(() => {
      instanceDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  
  // Handle tag filter change — triggers server re-fetch with new tags
  const handleTagsChange = (newTags) => {
    setSelectedTags(newTags);
    setTagsFilter(newTags);
    setDisplayLimit(12);
    setSelectedSurvey(null);
    setHospiceInstances([]);
  };
  
  // Handle bulk close all instances for a survey group
  const handleBulkCloseInstances = (surveyGroup) => {
    setConfirmModal({
      show: true,
      title: `⚠️ Close All Instances`,
      message: `You are about to close ALL ${surveyGroup.instanceCount} instances for "${surveyGroup.surveyTitle}"\n\nThis will:\n• Mark all instances as closed\n• Prevent further responses\n• Cannot be easily undone\n\nAre you sure you want to continue?`,
      onConfirm: () => performBulkClose(surveyGroup)
    });
  };
  
  // Perform the actual bulk close after confirmation
  const performBulkClose = async (surveyGroup) => {
    
    try {
      setLoading(true);

      await secureHelpers.admin.closeSurveyInstancesByGroup(surveyGroup.key);
      
      // Remove the closed group from the display
      setSurveyGroups(prevGroups => 
        prevGroups.filter(g => g.key !== surveyGroup.key)
      );
      
      // Clear selection if this was the selected survey
      if (selectedSurvey?.key === surveyGroup.key) {
        setSelectedSurvey(null);
        setHospiceInstances([]);
      }
      
      setConfirmModal(prev => ({ ...prev, show: false }));
      alert(`Successfully closed all ${surveyGroup.instanceCount} instances for "${surveyGroup.surveyTitle}"`);
      console.log('Bulk closed survey group:', surveyGroup.key);
    } catch (err) {
      console.error('Failed to bulk close instances:', err);
      setConfirmModal(prev => ({ ...prev, show: false }));
      alert('Failed to close instances. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle open/close for individual instance
  const handleToggleInstance = async (instance) => {
    const newState = !instance.isOpen;
    const action = newState ? 'open' : 'close';
    
    try {
      await secureHelpers.admin.toggleSurveyInstance(instance.instanceId, newState);
      
      // Update local state
      setHospiceInstances(prev => 
        prev.map(inst => 
          inst.instanceId === instance.instanceId 
            ? { ...inst, isOpen: newState }
            : inst
        )
      );
      
      console.log(`Successfully ${action}d instance ${instance.instanceId}`);
    } catch (err) {
      console.error(`Failed to ${action} instance:`, err);
      alert(`Failed to ${action} instance. Please try again.`);
    }
  };
  
  // Filter hospice instances by completion status
  const [completionFilter, setCompletionFilter] = useState('all');
  
  const filteredResponses = hospiceInstances.filter(hospice => {
    if (completionFilter === 'all') return true;
    if (completionFilter === 'completed') return hospice.progressStatus === 2 || hospice.progressStatus === "Complete" || hospice.progressStatus === "Completed";
    if (completionFilter === 'partial') return hospice.progressStatus === 1 || hospice.progressStatus === "InProgress";
    if (completionFilter === 'notStarted') return hospice.progressStatus === 0 || hospice.progressStatus === "NotStarted";
    return true;
  });



  return (
    <AdminLineItem title="Survey Tracking" user={user}>
      <div className="mb-4">
        <h1 style={{color: BRAND.PRIMARY, paddingBottom: "1.5rem", marginTop: "2rem"}}>Survey Tracking Dashboard</h1>
        <p style={{color: BRAND.BODY, fontSize: "var(--font-base)"}}>
          Monitor all surveys and their completion rates. Track user engagement and follow up with non-responders.
        </p>
      </div>
      
      {/* Survey Progress Overview */}
      <div className="card mb-4">
        <div className="card-header" style={{backgroundColor: BRAND.PRIMARY, borderRadius:"0px"}}>
          <h4 style={{color: "white", margin: "0.5rem 0", fontSize: "var(--font-4xl)"}}>
            All Surveys
          </h4>
          <p style={{color: "rgba(255,255,255,0.85)", margin: 0, fontSize: "var(--font-base)"}}>
            Click on a card to see detailed instances · Use the button below the currently displayed surveys to show more results
          </p>
        </div>
        <div className="card-body">
          {/* Search / filter bar — always visible - make sticky at top of screen and all else scrolls under it, with a brand orange bottom border */}

          <div className="row g-2 mb-3 align-items-end" style={{position: "sticky", top: 0, zIndex: 10, backgroundColor: "white", paddingTop: "1rem", paddingBottom: "1rem", borderBottom: `2px solid ${BRAND.PRIMARY}`}}>
            <div className="col-md-4">
              <label className="form-label  mb-1" style={{color: BRAND.BODY, fontSize: "var(--font-base)"}}>
                Search by name:
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Type to filter surveys…"
                value={titleSearch}
                onChange={e => { setTitleSearch(e.target.value); setDisplayLimit(12); }}
                style={{fontSize: "var(--font-base)"}}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label  mb-1" style={{color: BRAND.BODY, fontSize: "var(--font-base)"}}>
                Filter by tags (each tag you add is an 'and' filter):
                {loading && selectedTags.length > 0 && (
                  <span className="spinner-border spinner-border-sm ms-2" role="status" />
                )}
              </label>
              <SearchableSelect
                options={availableTags}
                value={selectedTags}
                onChange={handleTagsChange}
                placeholder="Pick tags to filter…"
                getLabel={tag => tag}
                getValue={tag => tag}
                multiple={true}
                allowCreate={false}
                maxHeight={200}
              />
              </div>
             {/* checkbox to include closed surveys */}
           <div className="col-md-3 d-flex align-items-center" style={{marginLeft: "1rem"}}>
              <FormControlLabel
                label="Include Closed Surveys"
                labelPlacement="start"
                disabled={selectedTags.length === 0}
                title={selectedTags.length === 0 ? "Add at least one tag to search closed surveys" : ""}
                control={
                  <Checkbox
                    checked={includeClosed}
                    onChange={(e) => setIncludeClosed(e.target.checked)}
                    size="large"
                    disabled={selectedTags.length === 0}
                    slotProps={{
                    input: { 'aria-label': 'controlled' },
                    }}
                  />
                }
              />
              {selectedTags.length === 0 && (
                <span style={{fontSize: "var(--font-sm)", color: "var(--bs-secondary)", marginLeft: "0.4rem"}}>
                  (Add a tag to enable)
                </span>
              )}
            </div>
            {(titleSearch || selectedTags.length > 0) && (
              <div className="col-md-2 d-flex align-items-end">
                <Button
                  variant="outline-secondary"
                  style={{fontSize: "var(--font-base)", width: "100%"}}
                  onClick={() => { setTitleSearch(''); handleTagsChange([]); }}
                >
                  Clear filters
                </Button>
              </div>
            )}
          </div>
          {(titleSearch || selectedTags.length > 0) && !loading && (
            <p style={{color: BRAND.BODY, fontSize: "var(--font-sm)", marginBottom: "0.75rem"}}>
              {titleFilteredSurveys.length === 0
                ? 'No surveys match your search.'
                : `Showing ${Math.min(displayLimit, titleFilteredSurveys.length)} of ${titleFilteredSurveys.length} matching survey${titleFilteredSurveys.length !== 1 ? 's' : ''}${selectedTags.length > 0 ? ' \xb7 Tags: ' + selectedTags.join(', ') : ''}`}
            </p>
          )}
          {loading ? (
            <div>
             <Spinner animation="border" role="status"></Spinner>
          <p style={{color: BRAND.BODY, fontSize: "16px", textAlign: "center", padding: "2rem"}}>
              Loading surveys...
            </p>
            </div>
          ) : displayedSurveys.length === 0 ? (
            <div style={{textAlign: "center", padding: "3rem"}}>
              <p style={{color: BRAND.BODY, fontSize: "18px", marginBottom: "1rem"}}>
                {selectedTags.length > 0 || titleSearch
                  ? 'No surveys match your search filters.'
                  : 'No active surveys found for monitoring.'}
              </p>
              {!selectedTags.length && !titleSearch && (
                <p style={{color: BRAND.BODY, fontSize: "14px"}}>
                  By default only active surveys are shown. Use the tag filter to search all surveys.
                </p>
              )}
            </div>
          ) : (
            <>
            <div className="row">
              {displayedSurveys.map(survey => (
              <div key={survey.key} className="col-md-6 col-lg-4 mb-3">
                <div 
                  className={`card h-100`}
                  onClick={() => handleSurveySelect(survey.key)}
                  style={{
                    cursor: 'pointer',
                    borderColor: selectedSurvey?.key === survey.key ? BRAND.PRIMARY : null,
                    borderWidth: selectedSurvey?.key === survey.key ? '3px' : '1px',
                    backgroundColor: 'white'
                  }}
                >
                  {/* Card header with sunshine gradient */}
                  <div className={`card-header ${styles.sunshine}`} style={{
                    borderRadius: "0px",
                    padding: "0.75rem 1rem"
                  }}>
                    <div className="mb-0" style={{
                      color: BRAND.DARK,
                      fontSize: "var(--font-subtitle)", 
                      fontFamily: "Neutraface-Demi",
                      fontWeight: "normal"
                    }}>
                      {survey.surveyTitle}
                    </div>
                  </div>
                  
                  <div className="card-body">
                    <div className="mb-3">
                      <span style={{color: BRAND.BODY, fontSize: "var(--font-base)"}}>
                        {survey.minStartDate ? 
                          `Started: ${formatDate(survey.minStartDate)} • Ends: ${formatDate(survey.endDate)}` : 
                          `Ends: ${formatDate(survey.endDate)}`}
                      </span>
                    </div>
                  </div>
                  <div className="card-footer" style={{backgroundColor: "white"}}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span style={{color: BRAND.BODY, fontSize: "var(--font-base)"}}>
                        {survey.instanceCount} instances
                      </span>
                      <span style={{color: BRAND.PRIMARY, fontSize: "var(--font-lg)"}}>
                        <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>End: {formatDate(survey.endDate)}</span>
                      </span>
                    </div>
                    
                    {/* Progress breakdown with percentage bar */}
                    {survey.progress && survey.progress.length > 0 && (() => {
                      // Get counts for each status (handle both numeric and string enum keys)
                      const notStartedItem = survey.progress.find(p => p.key === 0 || p.key === "NotStarted");
                      const inProgressItem = survey.progress.find(p => p.key === 1 || p.key === "InProgress");
                      // Backend inconsistently sends "Complete" OR "Completed" - handle both!
                      const completedItem = survey.progress.find(p => p.key === 2 || p.key === "Completed" || p.key === "Complete");
                      
                      const notStarted = notStartedItem?.value || 0;
                      const inProgress = inProgressItem?.value || 0;
                      const completed = completedItem?.value || 0;
                      const total = survey.instanceCount;
                      
                      // Check if progress counts add up to total
                      const progressTotal = notStarted + inProgress + completed;
                      const mismatch = progressTotal !== total;
                      
                      // Debug logging for mismatches
                      if (mismatch) {
                        console.warn(`⚠️ Progress mismatch for "${survey.surveyTitle}":`, {
                          total,
                          notStarted,
                          inProgress,
                          completed,
                          progressTotal,
                          difference: total - progressTotal,
                          rawProgress: survey.progress
                        });
                      }
                      
                      const completedPct = total > 0 ? (completed / total) * 100 : 0;
                      const inProgressPct = total > 0 ? (inProgress / total) * 100 : 0;
                      const notStartedPct = total > 0 ? (notStarted / total) * 100 : 0;
                      
                      return (
                        <>
                          {/* Progress bar */}
                          <div className="mb-2">
                            <ProgressBar style={{ height: "28px" }}>
                              <ProgressBar 
                                now={completedPct} 
                                style={{ backgroundColor: BRAND.DARK, fontSize: "var(--font-sm)" }}
                                label={completed > 0 ? `${Math.round(completedPct)}%` : ''}
                              />
                              <ProgressBar 
                                now={inProgressPct} 
                                style={{ backgroundColor: BRAND.CARD, fontSize: "var(--font-sm)", color: BRAND.DARK }}
                                label={inProgress > 0 ? `${Math.round(inProgressPct)}%` : ''}
                              />
                              <ProgressBar 
                                now={notStartedPct} 
                                style={{ backgroundColor: BRAND.PRIMARY, fontSize: "var(--font-sm)" }}
                                label={notStarted > 0 ? `${Math.round(notStartedPct)}%` : ''}
                              />
                            </ProgressBar>
                          </div>
                          
                          {/* Count breakdown */}
                          <div className="d-flex flex-wrap mb-2 progress-counts" style={{fontSize: 'var(--font-sm)', color: BRAND.BODY, gap: '0.75rem'}}>
                            <span style={{color: BRAND.PRIMARY, whiteSpace: 'nowrap', fontFamily: 'Neutraface-Demi'}}>✓ {completed} complete</span>
                            <span style={{color: BRAND.BODY, whiteSpace: 'nowrap', fontFamily: 'Neutraface-Demi'}}>⟳ {inProgress} in progress</span>
                            <span style={{color: BRAND.PRIMARY, whiteSpace: 'nowrap', fontFamily: 'Neutraface-Demi'}}>○ {notStarted} not started</span>
                          </div>
                          <style>{`
                            @media (min-width: 1400px) {
                              .progress-counts {
                                justify-content: space-between !important;
                              }
                            }
                          `}</style>
                          
                        </>
                      );
                    })()}
                    
                    {/* Bulk Close All Instances button */}
                    {survey.instanceCount > 0 && (
                      <Button
                        size="sm"
                        style={{
                          backgroundColor: BRAND.ALERT,
                          borderColor: BRAND.ALERT,
                          color: 'white',
                          width: '100%',
                          fontSize: 'var(--font-base)'
                        }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card selection
                          handleBulkCloseInstances(survey);
                        }}
                      >
                        Close All {survey.instanceCount} Instances
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Load More button */}
          {titleFilteredSurveys.length > displayLimit && (
            <div style={{textAlign: 'center', marginTop: '2rem'}}>
              <Button
                style={{
                  backgroundColor: BRAND.PRIMARY,
                  borderColor: BRAND.PRIMARY,
                  color: 'white',
                  fontSize: 'var(--font-lg)',
                  padding: '12px 32px',
                  fontFamily: 'Neutraface-Demi'
                }}
                onClick={() => setDisplayLimit(prev => prev + 12)}
              >
                Load More Surveys (Show 12 More)
              </Button>
            </div>
          )}
          </>
          )}
        </div>
      </div>
      
      {/* Instance Details */}
      {selectedSurvey && (
        <div ref={instanceDetailsRef} className="card mb-4" style={{marginTop: "3rem", scrollMarginTop: "2rem"}}>
          <div className="card-header" style={{backgroundColor: BRAND.PRIMARY, borderRadius:"0px"}}>
            <div className="d-flex justify-content-between align-items-center">
              <h4 style={{color: "white", margin: "0.5rem 0", fontSize: "var(--font-3xl)"}}>
                Instance Details: {selectedSurvey.surveyTitle}
              </h4>
              <Form.Select 
                style={{width: "250px", borderColor: BRAND.PRIMARY, fontSize: "16px", height: "42px"}} 
                value={completionFilter} 
                onChange={(e) => setCompletionFilter(e.target.value)}
              >
                <option value="all">All Recipients</option>
                <option value="completed">Completed</option>
                <option value="partial">Partially Completed</option>
                <option value="notStarted">Not Started</option>
              </Form.Select>
            </div>
          </div>
          <div className="card-body">
            {/* Survey Info Summary */}
            <div className="row mb-3" style={{backgroundColor: "#f8f9fa", padding: "1rem", borderRadius: "4px"}}>
              <div className="col-md-4">
                <div style={{color: BRAND.BODY, fontSize: "var(--font-sm)"}}>
                  <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Survey Dates:</span>
                </div>
                <div style={{fontSize: "var(--font-base)"}}>
                  {selectedSurvey.minStartDate !== '2000-01-01' && `Started: ${formatDate(selectedSurvey.minStartDate)} • `}
                  Ends: {formatDate(selectedSurvey.endDate)}
                </div>
              </div>
              <div className="col-md-4">
                <div style={{color: BRAND.BODY, fontSize: "var(--font-sm)"}}>
                  <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Total Instances:</span>
                </div>
                <div style={{fontSize: "var(--font-base)"}}>{selectedSurvey.instanceCount}</div>
              </div>
              <div className="col-md-4">
                <div style={{color: BRAND.BODY, fontSize: "var(--font-sm)"}}>
                  <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Assigned Roles:</span>
                </div>
                {selectedSurvey.roles && selectedSurvey.roles.length > 0 ? (
                  <ul style={{listStyle: "none", margin: 0, paddingLeft: "1.2rem", fontSize: "var(--font-base)"}}>
                    {selectedSurvey.roles.map(roleId => {
                      // Match role Id (string identifier) with permissions
                      const permission = permissions.find(p => p.id === roleId);
                      return (
                        <li key={roleId} style={{color: BRAND.DARK}}>
                          {permission?.label || roleId.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div style={{fontSize: "var(--font-base)", color: BRAND.BODY}}>No roles assigned</div>
                )}
              </div>
            </div>
            
            <div style={{maxHeight: "600px", overflowY: "auto"}}>
              <Table hover responsive className="mt-2">
                <thead className="sticky-top" style={{ backgroundColor: "white", color: BRAND.DARK }}>
                  <tr>
                    <th style={{fontSize: "var(--font-lg)"}}>Hospice</th>
                    <th style={{fontSize: "var(--font-lg)"}}>Progress</th>
                    <th style={{fontSize: "var(--font-lg)"}}>Last Active</th>
                    <th style={{fontSize: "var(--font-lg)"}}>Actions</th>
                    <th style={{fontSize: "var(--font-lg)"}}>Delete Instance</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResponses.map(user => (
                    <tr key={user.userId}>
                      <td style={{color: BRAND.DARK, fontSize: "var(--font-base)", fontWeight: "500"}}>{user.hospice}</td>
                      <td>
                        {(() => {
                          // Map progress status to badge
                          const status = user.progressStatus;
                          if (status === 2 || status === "Complete" || status === "Completed") {
                            return (
                              <span style={{
                                fontSize: "var(--font-base)", 
                                padding: "8px 16px",
                                backgroundColor: BRAND.ALERT,
                                color: "white",
                                borderRadius: "0.25rem",
                                display: "inline-block",
                                minWidth: "130px",
                                textAlign: "center"
                              }}>
                                Complete
                              </span>
                            );
                          } else if (status === 1 || status === "InProgress") {
                            return (
                              <span style={{
                                fontSize: "var(--font-base)", 
                                padding: "8px 16px",
                                backgroundColor: BRAND.CARD,
                                color: BRAND.DARK,
                                borderRadius: "0.25rem",
                                display: "inline-block",
                                minWidth: "130px",
                                textAlign: "center"
                              }}>
                                {getProgressLabel(status)}
                              </span>
                            );
                          } else {
                            return (
                              <span style={{
                                fontSize: "var(--font-base)", 
                                padding: "8px 16px",
                                backgroundColor: BRAND.BODY,
                                color: "white",
                                borderRadius: "0.25rem",
                                display: "inline-block",
                                minWidth: "130px",
                                textAlign: "center"
                              }}>
                                Not Started
                              </span>
                            );
                          }
                        })()}
                      </td>
                      <td style={{fontSize: "var(--font-base)"}}>{user.lastActive ? formatDate(user.lastActive) : "Not started"}</td>
                      <td>
                        <div style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #dee2e6"
                        }}>
                          <span style={{
                            fontSize: "var(--font-sm)",
                            fontFamily: user.isOpen ? "Neutraface-Demi" : "Neutraface-Book",
                            color: user.isOpen ? BRAND.PRIMARY : "#6c757d",
                            opacity: user.isOpen ? 1 : 0.6
                          }}>
                            Open
                          </span>
                          <Form.Check
                            type="switch"
                            id={`toggle-${user.instanceId}`}
                            checked={user.isOpen}
                            onChange={() => handleToggleInstance(user)}
                            style={{
                              transform: "scale(1.2)",
                              cursor: "pointer"
                            }}
                          />
                          <span style={{
                            fontSize: "var(--font-sm)",
                            fontFamily: !user.isOpen ? "Neutraface-Demi" : "Neutraface-Book",
                            color: !user.isOpen ? BRAND.ALERT : "#6c757d",
                            opacity: !user.isOpen ? 1 : 0.6
                          }}>
                            Closed
                          </span>
                        </div>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => {
                            setConfirmModal({
                              show: true,
                              title: `⚠️ Delete Survey Instance`,
                              message: `You are about to delete the instance for "${user.hospice}". This instance is "${getProgressLabel(user.progressStatus)}". This action cannot be undone. Are you sure you want to continue?`,
                              onConfirm: async () => {
                                try {
                                  await secureHelpers.admin.deleteSurveyInstance(user.instanceId);
                                  setHospiceInstances(prev => prev.filter(inst => inst.instanceId !== user.instanceId));
                                  setConfirmModal(prev => ({ ...prev, show: false }));
                                  alert(`Successfully deleted instance for "${user.hospice}"`);
                                  
                                } catch (err) {
                                  console.error('Failed to delete instance:', err);
                                  setConfirmModal(prev => ({ ...prev, show: false }));
                                  alert('Failed to delete instance. Please try again.');
                                }
                              }
                            });
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
          <div className="card-footer">
            <span style={{color: BRAND.BODY, fontSize: "var(--font-base)"}}>
              Showing {filteredResponses.length} of {hospiceInstances.length} instances
            </span>
          </div>
        </div>
      )}
      
      {/* Action buttons to manage surveys */}
      <div className="d-flex justify-content-between mb-4">
        
        <NavLink 
          to="/admin/surveyMaker/builder" 
          className="btn" 
          style={{
            backgroundColor: BRAND.PRIMARY, 
            color: "white",
            fontSize: "16px",
            padding: "8px 16px",
            marginLeft: "5rem"
          }}
        >
          Create New Survey
        </NavLink>
      </div>
      
      {/* Confirmation Modal */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, show: false }))}
        variant="danger"
        confirmText={confirmModal.confirmText || "Confirm"}
        cancelText="Cancel"
      />
    </AdminLineItem>
  );
};
